import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut,
  User
} from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  doc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { getAnalytics, logEvent, Analytics } from 'firebase/analytics';

import { Role } from './types';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// 1. Initialize Firebase App Singleton
const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();

// App Check disabled — ReCaptchaEnterpriseProvider interferes with Google SSO popup.
// Re-enable once App Check debug flow is verified locally first.

// 1b. Initialize Firebase Analytics (production only — no-op in dev)
let analytics: Analytics | null = null;
if (typeof window !== 'undefined' && firebaseConfig.measurementId) {
  try {
    analytics = getAnalytics(app);
  } catch {
    // Analytics may be unavailable in certain environments (e.g. blocked by adblocker)
  }
}

/**
 * Track a named event in Firebase Analytics.
 * Silently no-ops when analytics is unavailable (dev, adblockers, SSR).
 */
export const trackEvent = (
  eventName: string,
  params?: Record<string, string | number | boolean>
): void => {
  if (!analytics) return;
  try {
    logEvent(analytics, eventName, params);
  } catch {
    // Non-fatal — tracking failures must never affect user flows
  }
};

// 2. Initialize Firestore Singleton with Multi-Tab Persistence
let db: any;
try {
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager()
    })
  });
} catch (e) {
  db = getFirestore(app);
}

// 3. Initialize Auth Providers
const auth = getAuth(app);

// Basic identity only — calendar scopes used to be bundled in here too, but Firebase's
// signInWithPopup flow is implicit-grant and can never produce a refresh token regardless of
// scope, so they never actually enabled calendar sync (confirmed: zero refresh tokens were ever
// written). Calendar access is now its own explicit, post-login opt-in via a real server-side
// redirect flow (functions/src/https/initiateGoogleCalendarAuth.ts), requested only when a user
// deliberately clicks "Connect Google Calendar" in Settings — not bundled into every login.
const SCOPES = [
  'openid',
  'https://www.googleapis.com/auth/userinfo.profile',
  'https://www.googleapis.com/auth/userinfo.email',
];

/**
 * googleLoginProvider — for RETURNING users signing in.
 * Uses 'select_account' so Google shows the account picker but does NOT
 * re-show the consent/permission screen for scopes already granted.
 */
const googleLoginProvider = new GoogleAuthProvider();
SCOPES.forEach(scope => googleLoginProvider.addScope(scope));
googleLoginProvider.setCustomParameters({
  prompt: 'select_account',
  access_type: 'online',
});

/**
 * googleSignupProvider — for NEW users signing up for the first time.
 * Uses 'consent' so the full permission screen is shown exactly once,
 * ensuring the user explicitly grants all required scopes.
 */
const googleSignupProvider = new GoogleAuthProvider();
SCOPES.forEach(scope => googleSignupProvider.addScope(scope));
googleSignupProvider.setCustomParameters({
  prompt: 'consent',
  access_type: 'offline',
});

/**
 * 👤 User Profile Guard & Migration Engine
 * Ensures every user has a Firestore document structured to support:
 * 1. Personal Mode (Data nested in /users/{uid}/{collection})
 * 2. Household Mode (Data shared in /households/{hid}/{collection})
 */
export const ensureUserProfileDocument = async (user: User) => {
  if (!user.uid) return;

  const userRef = doc(db, 'users', user.uid);

  const profileData = {
    id: user.uid,
    email: user.email || '',
    name: user.displayName || user.email?.split('@')[0] || 'New User',
    avatar: user.photoURL || undefined,
    lastActive: serverTimestamp(),

    // --- DEFAULT FIELDS ---
    role: Role.MEMBER,
    preferredLanguage: 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    autoDetectTz: true,
    activeSparkCategories: [],
    dashboardLayout: [],
    activeHouseholdId: null, 
    memberOf: [],           
    googleOAuthAccepted: true, // Updated to true upon successful consent
  };

  try {
    await setDoc(userRef, profileData, { merge: true });
    await setDoc(userRef, { createdAt: serverTimestamp() }, { merge: true });
  } catch (error) {
    console.error("Critical: Profile sync failed. Verify Firestore Rules.", error);
    throw error;
  }
};

export {
  auth,
  db,
  analytics,
  googleLoginProvider,
  googleSignupProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut
};